import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Typography } from "@mhome/ui";
import InstructionInput from "../assistant/instruction-input";
import ChatSession from "./chat-session";

import { useEventEmitter } from "../ws/event-context";
import { useWebSocket } from "../ws/websocket";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  unreadChatMessagesState,
  userInfoState,
  activeScopeState,
} from "../global-state";
import {
  init,
  ackServiceMessage,
  query,
  stop,
} from "../service/assistant-service";
import { v4 as uuidv4 } from "uuid";
import {
  addOrUpdateChat,
  addReqMessageToChat,
  addServiceMessageToChat,
  addMessagesToChat,
  getChatId,
  getChatById,
  getLatestChat,
  clearChatHistory,
  getLanding,
  setLanding,
} from "./chat-history-storage";

const ChatView = ({ isChatVisible = true, onAssistantInteraction = null }) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isWaitingResponse, setIsWaitingResponse] = useState(false);
  const currentQueryId = useRef(null);
  const [chatSessions, setChatSessions] = useState([]);
  const chatSessionRefs = useRef(new Map());
  const curChatType = useRef("GENERAL");

  const pendingMessageRef = useRef(null);
  const currentSessionId = useRef(null);
  const [isSessionReady, setIsSessionReady] = useState(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true); // Track if user is at bottom
  const messagesContainerRef = useRef(null);
  const forceInitRef = useRef(false);
  const isProgrammaticScrollRef = useRef(false); // Track if we're programmatically scrolling
  const programmaticScrollTimeoutRef = useRef(null); // Timeout for resetting programmatic scroll flag

  // Message cache for batching storage operations
  const messageCache = useRef(new Map()); // queryId -> { chatId, messages[] }

  // Helper function to perform programmatic scroll
  const performProgrammaticScroll = useCallback((scrollFn) => {
    if (!messagesContainerRef.current) return;

    isProgrammaticScrollRef.current = true;

    // Clear any existing timeout
    if (programmaticScrollTimeoutRef.current) {
      clearTimeout(programmaticScrollTimeoutRef.current);
    }

    // Reset flag after a delay to handle smooth scrolling
    programmaticScrollTimeoutRef.current = setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 100);

    scrollFn();
  }, []);

  // Get the current (last) active session ref
  const getCurrentChatSessionRef = () => {
    if (!currentSessionId.current) return null;
    return chatSessionRefs.current.get(currentSessionId.current) || null;
  };

  // Additional hooks for chat message handling
  const { subscribe } = useEventEmitter();
  const { sendAndWaitWs } = useWebSocket();
  const [unreadChatMessages, setUnreadChatMessages] = useRecoilState(
    unreadChatMessagesState
  );
  const userInfo = useRecoilValue(userInfoState);
  const [activeScope] = useRecoilState(activeScopeState);

  const getCurrentChatId = async () => {
    return activeScope ? await getChatId(activeScope) : null;
  };

  // Cache management functions
  const cacheMessage = async (queryId, message) => {
    // Use Map's atomic get-or-create pattern to prevent race conditions
    if (!messageCache.current.has(queryId)) {
      // Initialize cache entry with current chatId when first message arrives
      const currentChatId = await getCurrentChatId();
      // Only set if it still doesn't exist (handles race condition)
      if (!messageCache.current.has(queryId)) {
        messageCache.current.set(queryId, {
          chatId: currentChatId,
          messages: [],
        });
        console.debug(
          "Initialized cache for queryId:",
          queryId,
          "with chatId:",
          currentChatId
        );
      }
    }

    // Get the cache entry and add the message
    const cacheEntry = messageCache.current.get(queryId);
    cacheEntry.messages.push(message);
  };

  const flushCachedMessages = async (queryId) => {
    const cacheEntry = messageCache.current.get(queryId);
    if (
      !cacheEntry ||
      !cacheEntry.messages ||
      cacheEntry.messages.length === 0
    ) {
      console.debug("No cached messages to flush for queryId:", queryId);
      return;
    }

    const { chatId, messages } = cacheEntry;
    if (chatId && activeScope) {
      await addMessagesToChat(activeScope, chatId, messages);
      messageCache.current.delete(queryId);
    } else {
      console.warn(
        "Cannot flush cached messages - no chatId or scopeId for queryId:",
        queryId
      );
    }
  };

  const handleAssistantMessage = async (payload) => {
    try {
      const message = JSON.parse(payload);
      const { queryId } = message;

      // Handle REQ_ASSISTANT messages specially
      if (message.type === "REQ_ASSISTANT") {
        try {
          const requestData = JSON.parse(message.content);
          const { text, mentions } = requestData;

          // Create a new block with REQ message
          const newBlock = {
            from: "assistant",
            queryId,
            messages: [
              {
                type: "REQ",
                text: text,
                mentions: mentions || {},
                step: 0,
                parentStep: -1,
              },
            ],
          };

          // Add the new block
          const currentRef = getCurrentChatSessionRef();
          if (currentRef) {
            currentRef.addNewBlock(queryId, newBlock);
            console.debug(
              "Session landOnly set to false - REQ_ASSISTANT block added"
            );
          }

          // Store REQ_ASSISTANT as a request message in IndexedDB
          const currentChatId = await getCurrentChatId();
          if (currentChatId) {
            const reqMessage = {
              queryId,
              requester: "assistant",
              text: text,
              file: null,
              mentions: mentions || {},
            };
            await addReqMessageToChat(activeScope, currentChatId, reqMessage);
          }

          currentQueryId.current = queryId;

          return;
        } catch (error) {
          console.error("Failed to parse REQ_ASSISTANT content:", error);
          return;
        }
      } else if (currentQueryId.current === queryId) {
        if (message.type === "COMPLETE") {
          setIsWaitingResponse(false);
          // Call markComplete function on the ChatBlock
          const currentRef = getCurrentChatSessionRef();
          if (currentRef) {
            currentRef.markBlockComplete(queryId);
          }

          // Flush cached messages to storage for this queryId
          await cacheMessage(queryId, message);
          setTimeout(async () => {
            await flushCachedMessages(queryId);
          }, 1000);
        } else if (message.type === "STOPPED") {
          // Call markStopped function on the ChatBlock
          const currentRef = getCurrentChatSessionRef();
          if (currentRef) {
            currentRef.markBlockStopped(queryId);
          }
        } else {
          await cacheMessage(queryId, message);
        }

        const currentRef = getCurrentChatSessionRef();
        if (currentRef) {
          currentRef.addMessageToBlock(queryId, message);
        }
      }
    } catch (error) {
      // pass
    }
  };

  const ackChatMessage = async (queryId, targetChatId, message, forceInit) => {
    const resp = await ackServiceMessage(
      sendAndWaitWs,
      targetChatId,
      queryId,
      message,
      forceInit
    );
    return resp;
  };

  const initChatSession = async () => {
    const targetChatId = await getCurrentChatId();
    const resp = await init(
      sendAndWaitWs,
      curChatType.current,
      targetChatId,
      userInfo?.setting?.debugMode || false,
      forceInitRef.current
    );
    forceInitRef.current = false;
    if (resp && resp.chatId) {
      if (resp.chatId !== targetChatId) {
        await handleRestartChatSession(resp);
      }
    } else {
      setErrorMessage("Assistant Error.");
    }
  };

  const handleChatMessage = useCallback(
    async (messageStr) => {
      console.debug("handleChatMessage", messageStr);
      const message = JSON.parse(messageStr);
      message.timestamp = Date.now();
      const targetChatId = await getCurrentChatId();
      const queryId = uuidv4();
      const resp = await ackChatMessage(
        queryId,
        targetChatId,
        message,
        forceInitRef.current
      );
      forceInitRef.current = false;
      if (!resp || resp.error) {
        return;
      }
      const newMessageBlock = {
        from: "service",
        queryId: queryId,
        messages: [
          {
            format: message.textMode.toUpperCase(),
            content: message.text,
            timestamp: message.timestamp,
          },
        ],
      };

      if (resp.chatId !== targetChatId) {
        await handleRestartChatSession(resp);
      }

      const currentRef = getCurrentChatSessionRef();
      if (currentRef) {
        currentRef.addNewBlock(queryId, newMessageBlock);
      }
      const currentChatId = await getCurrentChatId();
      console.debug("addServiceMessageToChat", currentChatId);
      await addServiceMessageToChat(activeScope, currentChatId, {
        ...message,
        queryId,
      });

      // Increment unread count when chat is not visible
      if (!isChatVisible) {
        setUnreadChatMessages(unreadChatMessages + 1);
      }
    },
    [isChatVisible, unreadChatMessages, setUnreadChatMessages, activeScope]
  );

  const handleNotificationMessage = useCallback(
    async (messageStr, type) => {
      const message = JSON.parse(messageStr);
      const queryId = uuidv4();

      if (type === "notification") {
        // Handle regular notification messages (system notifications are handled by notification-dialog-system)
        if (!message.isSystem) {
          const messageObj = {
            type: "NOTIFICATION",
            title: message.title || "Notification",
            content: message.message || message.content || "",
            timestamp: message.timestamp || Date.now(),
          };
          const newMessageBlock = {
            from: "notification",
            queryId: queryId,
            messages: [messageObj],
          };

          // Store in chat history
          let currentChatId = await getCurrentChatId();
          if (!currentChatId || currentSessionId.current === "landing") {
            forceInitRef.current = true;
            await initChatSession();
            currentChatId = await getCurrentChatId();
          }
          await addServiceMessageToChat(activeScope, currentChatId, {
            queryId,
            ...messageObj,
          });

          const currentRef = getCurrentChatSessionRef();
          if (currentRef) {
            currentRef.addNewBlock(queryId, newMessageBlock);
            console.debug("Added notification/dialog block to chat session");
          }

          // Increment unread count when chat is not visible
          if (!isChatVisible) {
            setUnreadChatMessages(unreadChatMessages + 1);
          }
        }
      } else if (type === "dialog") {
        // Handle dialog messages - create separate blocks for each UPSERT change
        if (message.changes && message.changes.length > 0) {
          for (const change of message.changes) {
            if (change.type === "UPSERT" && change.dialog) {
              const dialogQueryId = uuidv4(); // Unique queryId for each dialog
              const messageObj = {
                type: "DIALOG",
                title: change.dialog.message || "",
                timestamp: change.dialog.createdAt
                  ? change.dialog.createdAt * 1000
                  : Date.now(),
                timeout: change.dialog.timeout,
                id: change.dialog.id,
                groupId: change.dialog.groupId,
              };
              const dialogMessageBlock = {
                from: "notification",
                queryId: dialogQueryId,
                messages: [messageObj],
              };

              // Store each dialog separately in chat history
              let currentChatId = await getCurrentChatId();
              if (!currentChatId || currentSessionId.current === "landing") {
                forceInitRef.current = true;
                await initChatSession();
                currentChatId = await getCurrentChatId();
              }
              await addServiceMessageToChat(activeScope, currentChatId, {
                queryId: dialogQueryId,
                ...messageObj,
              });

              // Add each dialog as a separate block
              const currentRef = getCurrentChatSessionRef();
              if (currentRef) {
                currentRef.addNewBlock(dialogQueryId, dialogMessageBlock);
                console.debug("Added individual dialog block to chat session");
              }

              // Increment unread count when chat is not visible
              if (!isChatVisible) {
                setUnreadChatMessages((prev) => prev + 1);
              }
            }
          }
          return; // Early return since we handled everything above
        }
      }
    },
    [isChatVisible, unreadChatMessages, setUnreadChatMessages, activeScope]
  );

  useEffect(() => {
    const { unsubscribe } = subscribe((target, data) => {
      if (target === "/chat/message") {
        handleChatMessage(data.payload);
      } else if (target === "/assistant/message") {
        handleAssistantMessage(data.payload);
      } else if (target === "/notification") {
        handleNotificationMessage(data.payload, "notification");
      } else if (target === "/dialog") {
        handleNotificationMessage(data.payload, "dialog");
      }
    });

    return () => {
      unsubscribe();
    };
  }, [
    subscribe,
    handleChatMessage,
    handleAssistantMessage,
    handleNotificationMessage,
  ]);

  useEffect(() => {
    if (activeScope) {
      // Clear current displayed data before restarting chat session for new scope
      setChatSessions([]);
      chatSessionRefs.current.clear();
      messageCache.current.clear();
      currentSessionId.current = null;
      setIsWaitingResponse(false);
      currentQueryId.current = null;
      setErrorMessage(null);
      setLoading(false);

      // Try to get the latest chat first
      const loadLatestChat = async () => {
        try {
          // Check if we have a landing state and append landing session
          const landingPrevChatId = getLanding(activeScope, null);
          const latestChat = await getLatestChat(activeScope);
          if (landingPrevChatId || !latestChat) {
            forceInitRef.current = true;
            handleRestartChatSession({});
          } else {
            handleRestartChatSession(latestChat);
          }
        } catch (error) {
          console.error("Failed to load latest chat:", error);
          handleRestartChatSession({});
        }
      };

      loadLatestChat();
    }
  }, [activeScope]);

  const handleSendMessage = async ({
    instruction,
    audioFile,
    mentions,
    debugMode,
  }) => {
    await initChatSession();

    pendingMessageRef.current = {
      instruction,
      audioFile,
      mentions,
      debugMode,
      queryId: uuidv4(),
      timestamp: Date.now(),
    };
    const currentRef = getCurrentChatSessionRef();
    if (currentRef) {
      processPendingMessage();
    } else {
      setIsSessionReady(false);
    }
  };

  const processPendingMessage = useCallback(async () => {
    if (!pendingMessageRef.current) return;

    const { instruction, audioFile, mentions, debugMode, queryId, timestamp } =
      pendingMessageRef.current;

    currentQueryId.current = queryId;
    setErrorMessage(null);

    const newBlock = {
      from: "assistant",
      queryId,
      messages: [
        {
          type: "REQ",
          text: instruction,
          audioFile,
          mentions,
          step: 0,
          parentStep: -1,
          timestamp,
        },
      ],
    };

    const currentRef = getCurrentChatSessionRef();
    // Add block immediately using canvas ref
    if (currentRef) {
      currentRef.addNewBlock(queryId, newBlock);
    }

    setIsWaitingResponse(true);
    const currentChatId = await getCurrentChatId();

    // Store request message in IndexedDB
    if (currentChatId) {
      const reqMessage = {
        queryId,
        requester: "user",
        text: instruction,
        file: null,
        mentions: mentions || {},
        timestamp,
      };
      console.debug("addReqMessageToChat", currentChatId);
      await addReqMessageToChat(activeScope, currentChatId, reqMessage);
    }

    const resp = await query(
      sendAndWaitWs,
      currentChatId,
      queryId,
      { text: instruction, audioFile: audioFile, mentions },
      debugMode
    );
    if (resp.error) {
      if (resp.error === "SESSION_EXPIRED") {
        setErrorMessage("Assistant session is expired.");
      } else {
        setErrorMessage("Unknown assistant error.");
      }
      setIsWaitingResponse(false);
      currentQueryId.current = null;
      pendingMessageRef.current = null;
      return null;
    }

    pendingMessageRef.current = null; // Clear pending message
  }, [activeScope]);

  useEffect(() => {
    if (isSessionReady && pendingMessageRef.current) {
      processPendingMessage();
    }
  }, [isSessionReady, processPendingMessage]);

  const handleStopQuery = async () => {
    if (currentQueryId.current && sendAndWaitWs) {
      try {
        await stop(sendAndWaitWs, currentQueryId.current);
        await cacheMessage(currentQueryId.current, {
          type: "STOPPED",
          queryId: currentQueryId.current,
        });
        setTimeout(async () => {
          await flushCachedMessages(currentQueryId.current);
        }, 1000);
        setIsWaitingResponse(false);
        currentQueryId.current = null;
      } catch (error) {
        console.error("Error stopping generation:", error);
        setIsWaitingResponse(false);
        currentQueryId.current = null;
      }
    }
  };

  const handleClearHistory = async () => {
    try {
      await clearChatHistory();
      setChatSessions([]);
      currentSessionId.current = null;
      // Clear message cache
      messageCache.current.clear();
      forceInitRef.current = true;
      handleRestartChatSession({});
    } catch (error) {
      console.error("Failed to clear chat history:", error);
    }
  };

  const loadPreviousChat = useCallback(
    async (prevChatId) => {
      if (!prevChatId || !activeScope) return;

      // Check if this chat is already loaded in current sessions
      const isAlreadyLoaded = chatSessions.some(
        (session) => session.initData?.chatId === prevChatId
      );

      if (isAlreadyLoaded) {
        console.debug("Previous chat already loaded, skipping:", prevChatId);
        return;
      }

      try {
        const prevChatData = await getChatById(activeScope, prevChatId);
        if (prevChatData) {
          // Also fetch the previous chat's previous chat data for timestamp display
          let prevPrevChatData = null;
          if (prevChatData.prevChatId) {
            try {
              prevPrevChatData = await getChatById(
                activeScope,
                prevChatData.prevChatId
              );
            } catch (error) {
              console.warn(
                "Failed to load previous-previous chat data:",
                error
              );
            }
          }

          // Create a new session with the previous chat data
          const sessionId = uuidv4();
          const newSession = {
            id: sessionId,
            initData: {
              ...prevChatData,
              prevChatData: prevPrevChatData, // Cache for rendering
            },
            isLandingOnly: false,
          };

          // Prepend the previous session to the beginning of the sessions array
          setChatSessions((prevSessions) => [newSession, ...prevSessions]);
        }
      } catch (error) {
        console.error("Failed to load previous chat:", error);
        setErrorMessage("Failed to load previous chat.");
      }
    },
    [activeScope, chatSessions]
  );

  // Scroll detection effect
  useEffect(() => {
    const handleScroll = () => {
      // Ignore scroll events that we triggered programmatically
      if (isProgrammaticScrollRef.current) {
        return;
      }

      if (messagesContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } =
          messagesContainerRef.current;
        const isAtBottomNow = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold
        setIsAtBottom(isAtBottomNow);
        setShowScrollToBottom(!isAtBottomNow);
      }
    };

    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      // Check initial scroll position
      handleScroll();

      return () => {
        container.removeEventListener("scroll", handleScroll);
      };
    }
  }, [chatSessions.length]); // Re-run when new sessions are added

  // Auto-scroll to bottom when content changes if user was at bottom
  useEffect(() => {
    if (isAtBottom && messagesContainerRef.current) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        if (messagesContainerRef.current && isAtBottom) {
          performProgrammaticScroll(() => {
            messagesContainerRef.current.scrollTop =
              messagesContainerRef.current.scrollHeight;
          });
        }
      });
    }
  }, [chatSessions, isAtBottom, performProgrammaticScroll]); // Trigger when chat sessions change and user is at bottom

  // Auto-scroll when waiting for response changes (new messages arriving)
  useEffect(() => {
    if (isAtBottom && messagesContainerRef.current) {
      // Small delay to allow message rendering to complete
      const timeoutId = setTimeout(() => {
        if (messagesContainerRef.current && isAtBottom) {
          performProgrammaticScroll(() => {
            messagesContainerRef.current.scrollTop =
              messagesContainerRef.current.scrollHeight;
          });
        }
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [isWaitingResponse, isAtBottom, performProgrammaticScroll]);

  // MutationObserver to detect content changes and auto-scroll if at bottom
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const observer = new MutationObserver(() => {
      if (isAtBottom) {
        // Use requestAnimationFrame for smooth scrolling
        requestAnimationFrame(() => {
          if (container && isAtBottom) {
            performProgrammaticScroll(() => {
              container.scrollTop = container.scrollHeight;
            });
          }
        });
      }
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => observer.disconnect();
  }, [isAtBottom, performProgrammaticScroll]);

  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      performProgrammaticScroll(() => {
        messagesContainerRef.current.scrollTo({
          top: messagesContainerRef.current.scrollHeight,
          behavior: "smooth",
        });
      });
    }
  }, [performProgrammaticScroll]);

  const handleRestartChatSession = async (initData) => {
    setIsWaitingResponse(false);
    currentQueryId.current = null;
    setErrorMessage(null);
    setLoading(false);

    const sessionId = initData.chatId ? initData.chatId : "landing";

    // Check if sessionId already exists, skip if it does
    if (currentSessionId.current === sessionId) {
      setTimeout(() => {
        scrollToBottom();
      }, 100);
      return;
    }

    currentSessionId.current = sessionId;
    const curChatId = await getCurrentChatId();
    const landing =
      Object.keys(initData || {}).length === 0 || initData.type === "LANDING";
    const newSession = landing
      ? {
          id: "landing",
          initData: {
            chatId: "landing",
            prevChatId: curChatId || null,
          },
          isLandingOnly: true,
        }
      : {
          id: sessionId,
          initData: initData || {},
          isLandingOnly: false,
        };

    setChatSessions((prevSessions) => {
      if (prevSessions.length > 0) {
        const lastSession = prevSessions[prevSessions.length - 1];
        if (lastSession.isLandingOnly) {
          chatSessionRefs.current.delete(lastSession.id);
          return [...prevSessions.slice(0, -1), newSession];
        }
      }
      return [...prevSessions, newSession];
    });

    // Store chat data if provided
    if (initData.chatId && !landing) {
      if (initData.chatId !== curChatId) {
        const chatObj = {
          chatId: initData.chatId,
          prevChatId: initData.prevChatId || null,
          type: "GENERAL",
          serviceMessages: [],
          reqMessages: [],
          messages: [],
          createdAt: initData.createdAt || 0,
        };
        try {
          setLanding(activeScope, null);
          await addOrUpdateChat(activeScope, chatObj);
        } catch (error) {
          console.error("Failed to save chat data:", error);
        }
      }
    } else {
      setLanding(activeScope, curChatId);
    }

    // Auto-scroll to bottom after session restart
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  };

  // Render chat sessions with useMemo for performance
  const renderedChatSessions = useMemo(() => {
    const elements = [];

    chatSessions.forEach((session, index) => {
      const isLastSession = index === chatSessions.length - 1;
      const isFirstSession = index === 0;
      // Add divider before session
      if (isFirstSession) {
        // For first session, show "more history" divider if there's a prevChatId
        const prevChatId = session.initData?.prevChatId;
        if (prevChatId) {
          // Note: We'll need to cache the previous chat data in the session object
          // during loading to avoid async operations in render
          const prevChatData = session.initData?.prevChatData;
          const formatTimestamp = (millis) => {
            const date = new Date(millis);
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            const hours = String(date.getHours()).padStart(2, "0");
            const minutes = String(date.getMinutes()).padStart(2, "0");
            return `${month}-${day} ${hours}:${minutes}`;
          };

          const historyText = prevChatData?.createdAt
            ? `${formatTimestamp(prevChatData.createdAt)} - MORE HISTORY`
            : "MORE HISTORY";

          elements.push(
            <div
              key={`history-divider-${session.id}`}
              style={{
                marginLeft: "20px",
                marginRight: "20px",
                marginTop: "16px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  cursor: "pointer",
                }}
                onClick={() => loadPreviousChat(prevChatId)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = "0.8";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = "1";
                }}
              >
                <div
                  style={{
                    flex: 1,
                    height: "1px",
                    backgroundColor: "hsl(var(--border))",
                    opacity: 0.6,
                  }}
                />
                <Typography
                  variant="caption"
                  style={{
                    color: "hsl(var(--muted-foreground))",
                    fontSize: "0.7rem",
                    fontWeight: 500,
                    textTransform: prevChatData?.createdAt
                      ? "none"
                      : "uppercase",
                    letterSpacing: "0.05em",
                    paddingLeft: "8px",
                    paddingRight: "8px",
                    paddingTop: "4px",
                    paddingBottom: "4px",
                    borderRadius: "8px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "hsl(var(--accent))";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  {historyText}
                </Typography>
                <div
                  style={{
                    flex: 1,
                    height: "1px",
                    backgroundColor: "hsl(var(--border))",
                    opacity: 0.6,
                  }}
                />
              </div>
            </div>
          );
        } else if (session.initData?.createdAt) {
          // Show timestamp divider when no prevChatId but has timestamp
          const formatTimestamp = (millis) => {
            const date = new Date(millis);
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            const hours = String(date.getHours()).padStart(2, "0");
            const minutes = String(date.getMinutes()).padStart(2, "0");
            return `${month}-${day} ${hours}:${minutes}`;
          };

          elements.push(
            <div
              key={`timestamp-divider-${session.id}`}
              style={{
                marginLeft: "20px",
                marginRight: "20px",
                marginTop: "16px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    height: "1px",
                    backgroundColor: "hsl(var(--border))",
                    opacity: 0.6,
                  }}
                />
                <Typography
                  variant="caption"
                  style={{
                    color: "hsl(var(--muted-foreground))",
                    fontSize: "0.7rem",
                    fontWeight: 500,
                    textTransform: "none",
                    letterSpacing: "0.05em",
                    paddingLeft: "8px",
                    paddingRight: "8px",
                    paddingTop: "4px",
                    paddingBottom: "4px",
                    borderRadius: "8px",
                  }}
                >
                  {`${formatTimestamp(
                    session.initData.createdAt
                  )} - TOP OF HISTORY`}
                </Typography>
                <div
                  style={{
                    flex: 1,
                    height: "1px",
                    backgroundColor: "hsl(var(--border))",
                    opacity: 0.6,
                  }}
                />
              </div>
            </div>
          );
        }
      } else {
        // For non-first sessions, show "context reset" divider with timestamp if available
        const formatTimestamp = (millis) => {
          const date = new Date(millis);
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          const hours = String(date.getHours()).padStart(2, "0");
          const minutes = String(date.getMinutes()).padStart(2, "0");
          return `${month}-${day} ${hours}:${minutes}`;
        };

        const dividerText = session.initData?.createdAt
          ? `${formatTimestamp(session.initData.createdAt)} - CONTEXT RESET`
          : "CONTEXT RESET";

        elements.push(
          <div
            key={`divider-${session.id}`}
            style={{
              marginLeft: "20px",
              marginRight: "20px",
              marginTop: "16px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
              }}
            >
              <div
                style={{
                  flex: 1,
                  height: "1px",
                  backgroundColor: "hsl(var(--border))",
                  opacity: 0.6,
                }}
              />
              <Typography
                variant="caption"
                style={{
                  color: "hsl(var(--muted-foreground))",
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  textTransform: session.initData?.createdAt
                    ? "none"
                    : "uppercase",
                  letterSpacing: "0.05em",
                  paddingLeft: "8px",
                  paddingRight: "8px",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                  borderRadius: "8px",
                }}
              >
                {dividerText}
              </Typography>
              <div
                style={{
                  flex: 1,
                  height: "1px",
                  backgroundColor: "hsl(var(--border))",
                  opacity: 0.6,
                }}
              />
            </div>
          </div>
        );
      }

      elements.push(
        <div key={session.id}>
          <ChatSession
            ref={(ref) => {
              if (ref) {
                chatSessionRefs.current.set(session.id, ref);
              } else {
                chatSessionRefs.current.delete(session.id);
              }
              if (currentSessionId.current === session.id) {
                setIsSessionReady(true);
              }
            }}
            sessionData={session}
            loading={loading && isLastSession}
            errorMessage={isLastSession ? errorMessage : null}
            onAssistantInteraction={onAssistantInteraction}
          />
        </div>
      );
    });

    return elements;
  }, [
    chatSessions,
    loading,
    errorMessage,
    isWaitingResponse,
    onAssistantInteraction,
    loadPreviousChat,
  ]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        paddingTop: 0,
        paddingBottom: 0,
        background: "transparent",
        height: "100%",
      }}
    >
      <div
        ref={messagesContainerRef}
        style={{
          flexGrow: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          paddingBottom: "80px", // Space for input bar
        }}
      >
        {renderedChatSessions}
      </div>

      {/* Generating indicator and scroll to bottom */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingTop: "8px",
          paddingBottom: "8px",
          paddingLeft: "16px",
          paddingRight: "16px",
        }}
      >
        <div
          style={{
            height: "20px",
            width: "100%",
            backgroundColor: "transparent",
            borderRadius: "1px",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          {isWaitingResponse && (
            <div
              style={{
                position: "relative",
              }}
            >
              <style>
                {`
                  @keyframes dots {
                    0% { opacity: 0.3; }
                    50% { opacity: 1; }
                    100% { opacity: 0.3; }
                  }
                  .generating-dots::before {
                    content: "...";
                    color: hsl(var(--muted-foreground));
                    font-size: 16px;
                    font-weight: bold;
                    letter-spacing: 2px;
                    animation: dots 1.5s infinite;
                  }
                `}
              </style>
              <div className="generating-dots" />
            </div>
          )}
          {!isAtBottom && (
            <Typography
              variant="caption"
              style={{
                color: "hsl(var(--primary))",
                fontSize: "12px",
                fontWeight: "bold",
                cursor: "pointer",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                background: "hsl(var(--primary))",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                textShadow: "0 0 8px hsl(var(--primary) / 0.25)",
                border: "1px solid hsl(var(--primary) / 0.19)",
                borderRadius: "16px",
                paddingLeft: "8px",
                paddingRight: "8px",
                paddingTop: "2px",
                paddingBottom: "2px",
                transition: "all 0.2s ease",
              }}
              onClick={scrollToBottom}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.05)";
                e.currentTarget.style.textShadow =
                  "0 0 12px hsl(var(--primary) / 0.38)";
                e.currentTarget.style.border =
                  "1px solid hsl(var(--primary) / 0.31)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.textShadow =
                  "0 0 8px hsl(var(--primary) / 0.25)";
                e.currentTarget.style.border =
                  "1px solid hsl(var(--primary) / 0.19)";
              }}
            >
              goto latest messages
            </Typography>
          )}
        </div>
      </div>

      <div
        style={{
          position: "relative",
          bottom: 0,
          left: 0,
          right: 0,
          background: "transparent",
          zIndex: "auto",
        }}
      >
        <InstructionInput
          placeholder="Request here #quickcommand  /prompt  @mention"
          onSendMessage={handleSendMessage}
          onNewChat={() => {
            forceInitRef.current = true;
            handleRestartChatSession({});
          }}
          onStop={handleStopQuery}
          onClearHistory={handleClearHistory}
          isGenerating={isWaitingResponse}
        />
      </div>
    </div>
  );
};

export default ChatView;
